import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SESSION_QUERY_KEY, uaPortalUrl } from '@/features/auth/session';
import { apiRequest } from '@/lib/api-client';
import { IconBusy } from '@/lib/icons';

const launchSearchSchema = z.object({ code: z.string().optional(), next: z.string().optional() });

/**
 * Only a same-origin relative path is ever safe to honour here: `next`
 * reaches the browser as a plain query string value, so nothing stops a
 * crafted link from setting it to an absolute or protocol-relative URL even
 * though Unified Access itself only ever sends a same-origin path.
 */
export function safeNext(next: string | undefined): string | undefined {
  if (!next) return undefined;
  if (!next.startsWith('/') || next.startsWith('//')) return undefined;
  if (next.includes('://') || next.startsWith('/\\')) return undefined;
  return next;
}

/**
 * The web app sends the launch code to its own API, which redeems it server
 * to server and opens this app's own session cookie. The guard ref, not
 * `useState`, keeps this a single attempt even under StrictMode's
 * double-invoked effect.
 */
function LaunchPage(): React.JSX.Element {
  const { code, next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('auth');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!code) {
      globalThis.location.href = `${uaPortalUrl()}/login`;
      return;
    }

    const redeem = async (): Promise<void> => {
      try {
        await apiRequest('/auth/dashboard/launch', { method: 'POST', body: { code } });
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
        const destination = safeNext(next);
        if (destination) {
          globalThis.location.href = destination;
          return;
        }
        await navigate({ to: '/', replace: true });
      } catch {
        globalThis.location.href = `${uaPortalUrl()}/login`;
      }
    };
    void redeem();
  }, [code, next, navigate, queryClient]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3">
      <IconBusy className="size-6 text-muted-foreground" aria-hidden />
      <p role="status" className="text-sm text-muted-foreground">
        {t('launching')}
      </p>
    </div>
  );
}

export const Route = createFileRoute('/auth/launch')({
  validateSearch: launchSearchSchema,
  component: LaunchPage,
});
